import { create } from 'zustand';

export type SessionTokens = {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
  idToken?: string;
  deviceKey?: string | null;
  deviceGroupKey?: string | null;
};

export type PendingEmailChange = {
  id: string;
  previousEmail: string;
  newEmail: string;
  status: 'pending' | 'verified' | 'restored' | 'cancelled';
  createdAt: string | null;
  resolvedAt: string | null;
};

export type AuthenticatedUser = {
  id: string;
  username: string;
  email: string;
  role?: string | null;
  isRootAdmin?: boolean;
  lastLoginAt?: string | null;
  subscriptionTier?: string | null;
  subscriptionStatus?: string | null;
  setupState?: 'verification_pending' | 'payment_pending' | 'active' | null;
  marketingOptIn?: boolean;
  marketingOptInDate?: string | null;
  timezone?: string | null;
  requiresConsent?: boolean;
  restrictedAccess?: boolean;
  pendingConsents?: string[];
  emailVerified?: boolean;
  pendingEmailChange?: PendingEmailChange | null;
  pendingCheckoutIntent?: {
    tier: 'creator' | 'pro' | 'business';
    billingCycle: 'monthly' | 'annual';
    source: 'signup' | 'upgrade';
    intentOnly?: boolean;
    sessionStatus?: string | null;
    trial?: boolean;
  } | null;
  onboardingTarget?: 'platforms' | null;
};

export type AuthSession = {
  tokens: SessionTokens;
  user: AuthenticatedUser | null;
  sessionId?: string | null;
  onboardingTarget?: 'platforms' | null;
};

type AuthState = {
  tokens: SessionTokens | null;
  user: AuthenticatedUser | null;
  sessionId: string | null;
  setSession: (session: AuthSession | null) => void;
  updateTokens: (tokens: SessionTokens | null) => void;
  setUser: (user: AuthenticatedUser | null) => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  tokens: null,
  user: null,
  sessionId: null,
  setSession: (session) =>
    set({
      tokens: session?.tokens ?? null,
      user: session?.user ?? null,
      sessionId: session?.sessionId ?? null,
    }),
  updateTokens: (tokens) => set({ tokens }),
  setUser: (user) => set({ user }),
}));

export const getAccessToken = () => useAuthStore.getState().tokens?.accessToken ?? null;

export const getIdToken = () => useAuthStore.getState().tokens?.idToken ?? null;

export const getRefreshToken = () => useAuthStore.getState().tokens?.refreshToken ?? null;

export const getSessionId = () => useAuthStore.getState().sessionId ?? null;
